import { ChevronLeftIcon, ChevronRightIcon, ChevronsLeftIcon, ChevronsRightIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export const createPaginationStore = (pageIndex = 0, pageSize = 25, key = "") =>
  create<PaginationStore>()(
    persist(
      (set) => ({
        pageIndex,
        pageSize,
        setPageSize: (pageSize) => set({ pageSize, pageIndex: 0 }),
        setPageIndex: (pageIndex) => set({ pageIndex }),
      }),
      {
        name: `pagination-storage-${key}`,
        storage: createJSONStorage(() => localStorage),
      }
    )
  );

interface PaginationStore {
  pageSize: number;
  pageIndex: number;
  setPageSize: (pageSize: number) => void;
  setPageIndex: (pageIndex: number) => void;
}

interface DataTablePaginationProps {
  total: number;
  store: PaginationStore;
  /** Optional react-table instance; only the selected-row count is read. */
  table?: { getFilteredSelectedRowModel: () => { rows: unknown[] } };
}

export const DataTablePagination = ({
  total,
  store,
  table,
}: DataTablePaginationProps) => {
  const { pageSize, pageIndex, setPageSize, setPageIndex } = store;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const isLastPage = pageIndex === pageCount - 1;

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {table
          ? `${table.getFilteredSelectedRowModel().rows.length} of ${total} row(s) selected.`
          : `${total} row(s)`}
      </div>
      <div className="flex items-center space-x-3 sm:space-x-6 lg:space-x-8">
        {/* Page-size picker is a desktop refinement — hidden on phones so the
            pager buttons stay reachable without horizontal overflow. */}
        <div className="hidden items-center space-x-2 md:flex">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={`${pageSize}`}
            onValueChange={(value) => setPageSize(Number(value))}
          >
            <SelectTrigger size="sm" className="w-[70px]">
              <SelectValue placeholder={`${pageSize}`} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 25, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-center whitespace-nowrap text-sm font-medium md:w-[100px]">
          Page {pageIndex + 1} of {pageCount}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => setPageIndex(0)}
            disabled={pageIndex === 0}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeftIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => setPageIndex(pageIndex - 1)}
            disabled={pageIndex === 0}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => setPageIndex(pageIndex + 1)}
            disabled={isLastPage}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => setPageIndex(pageCount - 1)}
            disabled={isLastPage}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRightIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataTablePagination;
